import { useEffect, useMemo, useState } from 'react';

import {
  ExternalLoyaltyManualOfferBrandFilter,
  External_Loyalty_Manual_Offer_Period,
  useGetExternalLoyaltyManualOfferBrandsQuery,
} from '@occc/graphql';

import { OfferTableType } from '../tobacco-api-offers.types';

const SEARCH_DEBOUNCE_MS = 300;

const buildExternalLoyaltyBrandFilter = (
  tableType: OfferTableType,
  search: string,
): ExternalLoyaltyManualOfferBrandFilter => {
  const filter: ExternalLoyaltyManualOfferBrandFilter = {};
  if (tableType === 'active') {
    filter.periodEquals = External_Loyalty_Manual_Offer_Period.Current;
    filter.isInActivate = true;
  } else if (tableType === 'inactive') {
    filter.periodEquals = External_Loyalty_Manual_Offer_Period.Current;
    filter.isInActivate = false;
  } else {
    filter.periodEquals = External_Loyalty_Manual_Offer_Period.Next;
  }
  const trimmed = search.trim();
  if (trimmed.length > 0) {
    filter.search = trimmed;
  }
  return filter;
};

type UseTobaccoApiOfferBrandsSearchParams = {
  tableType: OfferTableType;
  searchInput: string;
  isOpen: boolean;
};

export const useTobaccoApiOfferBrandsSearch = ({
  tableType,
  searchInput,
  isOpen,
}: UseTobaccoApiOfferBrandsSearchParams) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setDebouncedSearch('');
      return;
    }
    const trimmed = searchInput.trim();
    if (trimmed.length === 0) {
      setDebouncedSearch('');
      return;
    }
    const id = window.setTimeout(() => setDebouncedSearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput, isOpen]);

  const filter = useMemo(
    () => buildExternalLoyaltyBrandFilter(tableType, debouncedSearch),
    [tableType, debouncedSearch],
  );

  const { data, loading, error } = useGetExternalLoyaltyManualOfferBrandsQuery({
    variables: { filter },
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });

  const brands = useMemo(() => {
    if (!isOpen || !data) return [];

    return data.externalLoyaltyManualOfferBrands
      .map((brand) => brand.name)
      .sort((a, b) => a.localeCompare(b));
  }, [data, isOpen]);

  return { brands, loading: isOpen && loading, error };
};
